// React & Core Hooks
import React, { useEffect, useState } from 'react';

// Routing Hooks & Navigation
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

// Ant Design Icons
import {
  CheckOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  FilterFilled,
  InfoCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';

// Ant Design Components
import {
  App,
  Badge,
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Popover,
  Row,
  Table,
  Tag,
  Typography,
} from 'antd';

// Date/Time Utilities
import dayjs from 'dayjs';
import moment from 'moment';

// Custom/App Components
import { Loading } from 'app/components/Loading';
import EditRecordModal from 'app/components/Modals/AttendanceHistory/EditRecordModal';

// Custom Hooks & Helpers
import { cellBadge, exportToExcel } from 'app/helpers/attendanceFunctions';
import { useAsyncState } from 'app/hooks/useAsyncState';
import useDisclosure from 'app/hooks/useDisclosure';

// Redux Actions
import { GetInitiativeRoles } from 'app/redux/actions/initiatives';

// Destructured Components
const { Title } = Typography;

const AttendanceTable = ({
  attendanceDataLoading,
  attendanceData,
  initiative,
  initiativeLabel,
  setInitiative,
  searchParams,
  setSearchParams,
  showDrawer,
  fetchData,
}) => {
  const { modal } = App.useApp();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [routerSearchParams, setRouterSearchParams] = useSearchParams();

  const { success, data, start } = useAsyncState();
  const [addModalOpen, addModalHandlers] = useDisclosure();

  const [columns, setColumns] = useState([]);
  const [dataSource, setDataSource] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState({});

  useEffect(() => {
    if (attendanceData) {
      setDataSource(attendanceData.rows);
      const dates = attendanceData.columns;

      const tableColumns = [
        {
          title: 'Usher ID',
          key: 'usher-id',
          align: 'center',
          width: '10%',
          fixed: 'left',
          render: (_, record) => record.usherID,
        },
        {
          title: 'Full Name',
          key: 'usher-name',
          align: 'center',
          width: '10%',
          fixed: 'left',
          render: (_, record) => (
            <div style={{ flexDirection: 'column' }}>
              <Link
                to={`/ushers/${record.usherID}/profile/`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {record.usherName}
              </Link>
              <br />
              <Typography.Text type="secondary">
                {record?.usherRole}
              </Typography.Text>
            </div>
          ),
        },
        ...dates.map((date, index) => ({
          title: date,
          dataIndex: date,
          key: date,
          align: 'center',
          width: '5%',
          render: (_, record) => {
            const matchingRecord = record.attendanceRecords[index];

            let checkInDateTime = 'N/A';
            let checkOutDateTime = 'N/A';

            if (matchingRecord?.[date]) {
              const att = matchingRecord?.[date];

              checkInDateTime = dayjs(
                `${att?.checkInDate} ${att?.checkInTime}`,
                'YYYY-MM-DD hh:mm:ss',
              ).format('DD/MM/YYYY h:mm:ss A');

              checkOutDateTime = dayjs(
                `${att?.checkOutDate} ${att?.checkOutTime}`,
                'YYYY-MM-DD hh:mm:ss',
              ).format('DD/MM/YYYY h:mm:ss A');
            }

            return (
              <Popover
                content={
                  <>
                    <strong>Attendance Record:</strong>
                    <ul>
                      <li>Check In Time: {checkInDateTime}</li>
                      <li>Check Out Time: {checkOutDateTime}</li>
                    </ul>

                    <strong>Assigned Shift:</strong>
                    <ul>
                      <li>Shift Start: {record.assignedShift?.startingTime}</li>
                      <li>Shift End: {record.assignedShift?.endingTime}</li>
                    </ul>

                    <Button
                      className="me-3"
                      onClick={() => {
                        window.open(
                          `/attendance/mark-attendance?search=${record?.usherCivilIDNumber}`,
                          '_blank',
                        );
                      }}
                    >
                      Attendance History
                    </Button>

                    {!matchingRecord?.[date]?.checkInTime &&
                      !matchingRecord?.[date]?.checkOutTime &&
                      record.assignedShift && (
                        <Button
                          onClick={() => {
                            setSelectedRecord({
                              date,
                              initiative_usher: record?.initiativeUsherId,
                            });
                            addModalHandlers.open();
                          }}
                        >
                          Add Attendance
                        </Button>
                      )}
                  </>
                }
              >
                {cellBadge(matchingRecord?.[date], record.assignedShift)}
              </Popover>
            );
          },
        })),
        {
          title: 'Approved Shifts',
          key: 'shifts',
          align: 'center',
          width: '5%',
          fixed: 'right',
          render: (_, record) => record.totalApprovedShifts,
        },
        {
          title: 'Reward (KWD)',
          key: 'reward',
          align: 'center',
          width: '5%',
          fixed: 'right',
          render: (_, record) => record?.usherReward || 0,
        },
      ];

      setColumns(tableColumns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceData]);

  async function getInitiativeRoles() {
    start();
    const res = await dispatch(GetInitiativeRoles(initiative));
    if (res.data) {
      success(res.data);
    }
  }

  const updateParams = updates => {
    const newParams = new URLSearchParams(routerSearchParams.toString());
    Object.entries(updates).forEach(([key, value]) => {
      newParams.set(key, value);
    });
    setRouterSearchParams(newParams, { replace: true });
  };

  useEffect(() => {
    if (Boolean(initiative)) {
      getInitiativeRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiative]);

  return (
    <>
      <Row justify="space-between" align="middle" className="mb-3">
        <Col>
          <div
            style={{ display: 'flex', alignItems: 'center', height: '100%' }}
          >
            <Title level={3} className="mb-0">
              Attendance History List
            </Title>
          </div>
        </Col>

        <Col>
          {Boolean(initiative) && (
            <Button
              type="primary"
              style={{ height: '40px' }}
              className="me-3"
              onClick={() => {
                modal.info({
                  width: '70%',
                  title: 'Roles Information',
                  content: (
                    <>
                      <Table
                        scroll={{ x: true }}
                        dataSource={data}
                        columns={[
                          {
                            title: 'Role',
                            key: 'title',
                            render: (text, record) => {
                              return record.title;
                            },
                          },
                          {
                            title: 'Probation Hours',
                            key: 'probation_hours',
                            render: (text, record) => {
                              return record.probation_hours;
                            },
                          },
                          {
                            title: 'Probation Hour Rate',
                            key: 'probation_hour_rate',
                            render: (text, record) => {
                              return record.probation_hour_rate;
                            },
                          },
                          {
                            title: 'After Probation Rate',
                            key: 'after_probation_rate',
                            render: (text, record) => {
                              return record.after_probation_rate;
                            },
                          },
                        ]}
                      />
                    </>
                  ),
                });
              }}
            >
              <InfoCircleOutlined style={{ fontSize: '18px' }} />
            </Button>
          )}
          <Button
            type="primary"
            style={{ height: '40px' }}
            className="me-3"
            onClick={() => navigate('/attendance/mark-attendance')}
          >
            <CheckOutlined style={{ fontSize: '18px' }} />
          </Button>
          <Button
            type="primary"
            style={{ height: '40px' }}
            className="me-3"
            disabled={!initiative}
            onClick={() => exportToExcel(dataSource, columns)}
          >
            <DownloadOutlined style={{ fontSize: '18px' }} />
          </Button>

          <Button
            type="primary"
            style={{ height: '40px' }}
            onClick={showDrawer}
          >
            <FilterFilled style={{ fontSize: '18px' }} />
          </Button>
        </Col>
      </Row>

      <Flex gap="4px 12px" wrap="wrap" className="mb-4">
        <Badge color="red" text="No attendance record" />
        <Badge color="orange" text="Did not check in or out" />
        <Badge color="green" text="Normal Hours" />
        <Badge color="yellow" text="Missing Hours" />
        <Badge color="geekblue" text="Extra Hours" />
      </Flex>

      <Flex gap="4px 12px" wrap="wrap" className="mb-4">
        {initiative && (
          <Tag
            color="cyan"
            closeIcon={<CloseCircleOutlined />}
            onClose={() => setInitiative(null)}
          >
            initiative: {initiativeLabel}
          </Tag>
        )}
        {routerSearchParams.get('start_date') &&
          routerSearchParams.get('end_date') && (
            <Tag
              color="volcano"
              closeIcon={<CloseCircleOutlined />}
              onClose={() => {
                const newParams = {
                  ...searchParams,
                  start_date: moment().startOf('month').format('YYYY-MM-DD'),
                  end_date: moment().endOf('month').format('YYYY-MM-DD'),
                };
                setSearchParams(newParams);
                updateParams(newParams);
              }}
            >
              {moment(routerSearchParams.get('start_date')).format(
                'DD-MM-YYYY',
              )}{' '}
              -{moment(routerSearchParams.get('end_date')).format('DD-MM-YYYY')}
            </Tag>
          )}
      </Flex>
      {initiative && (
        <Form
          name="search"
          style={{ width: '100%' }}
          onFinish={vals => {
            updateParams({ search: vals.search });
            setSearchParams({ ...searchParams, ...vals });
          }}
        >
          <Flex gap="4px 12px">
            <Form.Item initialValue={searchParams.search} name="search">
              <Input
                style={{ width: '100%' }}
                addonBefore={<SearchOutlined />}
                placeholder="Search "
              />
            </Form.Item>
            <Button type="primary" htmlType="submit">
              Search
            </Button>
            <Button
              onClick={() => {
                modal.confirm({
                  content: (
                    <AttendanceTable.FiltersPopup updateParams={updateParams} />
                  ),
                  onOk: () => {
                    setSearchParams({
                      ...searchParams,
                      ...Object.fromEntries(routerSearchParams),
                    });
                  },
                  onCancel: () => {
                    updateParams({
                      start_date: searchParams.start_date,
                      end_date: searchParams.end_date,
                    });
                  },
                });
              }}
            >
              Filters
            </Button>
          </Flex>
        </Form>
      )}
      {attendanceDataLoading ? (
        <Loading />
      ) : !initiative ? (
        <Table
          dataSource={null}
          locale={{
            emptyText:
              'kindly select an initiative to be able to see the related attendance data.',
          }}
          columns={[]}
        />
      ) : (
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30'],
          }}
          scroll={{ x: true }}
        />
      )}
      <EditRecordModal
        show={addModalOpen}
        Close={addModalHandlers.close}
        data={selectedRecord}
        mode={'add'}
        fetchData={fetchData}
      />
    </>
  );
};

export default AttendanceTable;

AttendanceTable.FiltersPopup = ({ updateParams }) => {
  return (
    <Flex>
      <DatePicker
        style={{ width: '100%' }}
        picker="month" // only month and year selection
        onChange={date => {
          if (date) {
            const startDate = date.startOf('month').format('YYYY-MM-DD');
            const endDate = date.endOf('month').format('YYYY-MM-DD');

            updateParams({
              start_date: startDate,
              end_date: endDate,
            });
          }
        }}
        format="MMMM YYYY" // show month name and year
      />
    </Flex>
  );
};
